import * as Sentry from '@sentry/react';

import { getConfig } from '../../config';

import type { ExtrasAndTags } from './types';

const DEFAULT_SENTRY_DSN =
	'https://8b156bc879d5d53284bbaab24c837162@o55978.ingest.us.sentry.io/4511232360710144';
const DEFAULT_PACKAGE_NAME = 'twg-microsite';
const DEFAULT_TEAM_NAME = 'twg-graphyte';
const DEFAULT_PRODUCT = 'twgMicrosite';
const DEFAULT_LOGGER = 'logger.default';

const getEnvironment = (): string => {
	const { env } = getConfig();
	switch (env) {
		case 'production':
			return 'production';
		case 'staging':
			return 'staging';
		case 'development':
		case 'local':
		default:
			return 'dev';
	}
};

/**
 * Initialises the global Sentry client for twg-microsite.
 *
 * - Disabled in dev environments (localhost / development / local) to avoid noise.
 * - Uses `beforeSend` to only capture events that were explicitly tagged via
 *   `logException` / `logMessage`, preventing unwanted global errors from being sent.
 * - Sends default PII (e.g. IP addresses) as requested by Sentry project config.
 */
export const initSentry = (): void => {
	const environment = getEnvironment();

	Sentry.init({
		dsn: DEFAULT_SENTRY_DSN,
		environment,
		enabled: environment !== 'dev',
		beforeSend: (event) => {
			// Only capture events that were explicitly tagged via logException/logMessage
			if (event.tags?.name === undefined) {
				return null;
			}
			return event;
		},
		initialScope: {
			user: {
				id: 'UNKNOWN_AAID',
			},
			tags: {
				packageName: DEFAULT_PACKAGE_NAME,
				teamName: DEFAULT_TEAM_NAME,
				product: DEFAULT_PRODUCT,
				logger: DEFAULT_LOGGER,
			},
		},
	});
};

const setTagsFromExtras = (scope: Sentry.Scope, extrasAndTags: ExtrasAndTags): void => {
	scope.setTag('name', DEFAULT_PRODUCT);

	if (extrasAndTags.loggerName !== undefined && extrasAndTags.loggerName.length !== 0) {
		scope.setTag('logger', `logger.${extrasAndTags.loggerName}`);
	}
	if (extrasAndTags.accountId !== undefined && extrasAndTags.accountId.length !== 0) {
		scope.setUser({ id: extrasAndTags.accountId });
	}
	if (extrasAndTags.teamName !== undefined && extrasAndTags.teamName.length !== 0) {
		scope.setTag('teamName', extrasAndTags.teamName);
	}
	if (extrasAndTags.touchpointId !== undefined && extrasAndTags.touchpointId.length !== 0) {
		scope.setTag('touchpointId', extrasAndTags.touchpointId);
	}
};

/**
 * Sets the Atlassian account ID on the global Sentry scope so all
 * subsequent events include the user identity.
 *
 * Call this once the user has authenticated (e.g. in UserProvider).
 */
export const setSentryUser = (accountId: string): void => {
	Sentry.setUser({ id: accountId });
};

/**
 * Captures an exception with optional structured metadata (extras & tags).
 *
 * The `extrasAndTags` object is attached both as Sentry extras (for search)
 * and selectively promoted to tags (for filtering/alerting).
 */
export const logException = (exception: Error | unknown, extrasAndTags?: ExtrasAndTags): void => {
	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureException(exception);
	});
};

/**
 * Captures an informational message with optional structured metadata.
 */
export const logMessage = (message: string, extrasAndTags?: ExtrasAndTags): void => {
	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureMessage(message);
	});
};
